import React, { FC, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button, Flex, Heading, Stack } from '@chakra-ui/react';

import { IoMdListBox } from 'react-icons/io';
import { MdHardware, MdInventory2, MdLocalDrink } from 'react-icons/md';

import useQueryParams from 'hooks/useQueryParams';

import Items from 'routes/portal/Items';
import Cases from 'routes/portal/Cases';
import { PackagesCreatedTable } from 'routes/portal/PackagesCreated/PackagesCreatedTable';
import { EquipmentInventoryTable } from 'components/modules/EquipmentInventoryTable';

import { UserPill } from 'components/core/UserPill';

import {
  UnitTrackingTab,
  UnitTrackingViewProps,
  isUnitTrackingTab,
} from './UnitTracking.props';

const TABS: {
  key: UnitTrackingTab;
  label: string;
  icon: JSX.Element;
}[] = [
  { key: 'item', label: 'Items', icon: <MdLocalDrink /> },
  { key: 'case', label: 'Cases', icon: <MdInventory2 /> },
  { key: 'packg', label: 'Packages', icon: <IoMdListBox /> },
  { key: 'equipment', label: 'Equipment', icon: <MdHardware /> },
];

const UnitTrackingView: FC<UnitTrackingViewProps> = () => {
  const queryUrl = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Seeded in the initializer rather than an effect: an effect would render
  // the default tab first, and that table's useUrlSharing would rewrite the
  // query string before we got a chance to read compType back out of it.
  const [tab, setTab] = useState<UnitTrackingTab>(() =>
    queryUrl.compType && isUnitTrackingTab(queryUrl.compType)
      ? queryUrl.compType
      : 'item',
  );

  const onSelectTab = (next: UnitTrackingTab) => {
    if (next === tab) return;

    // Drop the outgoing table's filters before the next one mounts. The three
    // tables share param names (sku, venue, status), so without this the
    // incoming table can read the previous tab's filters back out of the URL
    // and adopt them. compType is kept so the URL is never without it.
    navigate(`${location.pathname}?compType=${next}`, { replace: true });
    setTab(next);
  };

  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading fontSize="24px">Unit Tracking</Heading>
        <Flex align="flex-end">
          <UserPill />
        </Flex>
      </Flex>
      <Flex h="20px" />
      <Stack mb={6} isInline>
        {TABS.map(({ key, label, icon }) => (
          <Button
            key={key}
            w="180px"
            borderRadius="5px"
            size="lg"
            leftIcon={icon}
            variant={tab === key ? 'solid' : 'outline'}
            onClick={() => onSelectTab(key)}>
            {label}
          </Button>
        ))}
      </Stack>

      {tab === 'item' ? (
        <Items compType="item" embedded />
      ) : tab === 'case' ? (
        <Cases compType="case" embedded />
      ) : tab === 'packg' ? (
        <PackagesCreatedTable
          compType="packg"
          isDeleting={false}
          onDelete={() => undefined}
        />
      ) : (
        <EquipmentInventoryTable />
      )}
    </Flex>
  );
};

export default UnitTrackingView;
